<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useReceiptScan } from '@/composables/useReceiptScan'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Purchase',
  },
})

const route = useRoute()
const router = useRouter()
const scanId = route.params.id

const { scan, loading, error } = useReceiptScan(scanId)
const { loading: converting, errors: convertErrors, request: convertRequest } = useApiRequest()

// ─── Daftar ingredient untuk autocomplete nama item ────────────────────────
const ingredients = ref([])
const ingredientsLoading = ref(true)

onMounted(async () => {
  try {
    const res = await useApi('/api/v1/ingredients', { query: { per_page: 500 } }).get()
    const body = res?.data ?? res
    const list = Array.isArray(body) ? body : (body?.data ?? [])
    ingredients.value = list.map(i => ({ title: i.name, value: i.id }))
  } catch (err) {
    console.error('[ReceiptScanReview] loadIngredients error', err)
  } finally {
    ingredientsLoading.value = false
  }
})

// ─── Badge match_status ─────────────────────────────────────────────────────
const STATUS_META = {
  auto_matched:    { color: 'success', label: 'Otomatis' },
  suggested:       { color: 'warning', label: 'Perlu Dicek' },
  unmatched:       { color: 'error',   label: 'Tidak Dikenali' },
  manual_override: { color: 'info',    label: 'Manual' },
}

const statusColor = status => STATUS_META[status]?.color ?? 'secondary'
const statusLabel = status => STATUS_META[status]?.label ?? status

// ─── Simpan koreksi per baris (dipanggil saat blur / ganti pilihan) ────────
const savingItemId = ref(null)

const saveItem = async item => {
  savingItemId.value = item.id
  try {
    const payload = {
      extracted_qty:          item.extracted_qty,
      extracted_unit_text:    item.extracted_unit_text,
      extracted_unit_price:   item.extracted_unit_price,
      matched_ingredient_id:  item.matched_ingredient_id,
    }

    const res = await useApi(`/api/v1/purchase/receipt-scans/${scanId}/items/${item.id}`).patch({ body: payload })
    const body = res?.data ?? res
    const updated = body?.data ?? body

    // Sinkronkan hasil dari server (mis. match_status berubah jadi manual_override)
    Object.assign(item, updated)
  } catch (err) {
    console.error('[ReceiptScanReview] saveItem error', err)
  } finally {
    savingItemId.value = null
  }
}

const onIngredientChange = (item, newIngredientId) => {
  const selected = ingredients.value.find(opt => opt.value === newIngredientId)

  item.matched_ingredient_id = newIngredientId
  if (selected) item.extracted_name = selected.title

  saveItem(item)
}

// ─── Convert ke Purchase Receiving ─────────────────────────────────────────
const hasUnmatched = computed(() =>
  (scan.value?.items ?? []).some(item => item.match_status === 'unmatched' || !item.matched_ingredient_id)
)

const convertDisabledReason = computed(() => {
  if (scan.value?.status === 'applied') return 'Scan ini sudah pernah dikonversi menjadi Purchase Receiving.'
  if (!scan.value?.items?.length) return 'Tidak ada item untuk dikonversi.'
  if (hasUnmatched.value) return 'Masih ada item berstatus "Tidak Dikenali" — lengkapi dulu bahan bakunya di atas.'
  return null
})

const convert = async () => {
  const result = await convertRequest(() =>
    useApi(`/api/v1/purchase/receipt-scans/${scanId}/convert`).post()
  )

  if (result !== null) {
    const body = result?.data ?? result
    const receivingId = body?.purchase_receiving_id ?? body?.data?.purchase_receiving_id

    router.push({ name: 'purchase-receivings-id', params: { id: receivingId } })
  }
}

// ─── Zoom foto struk ────────────────────────────────────────────────────────
const zoomDialog = ref(false)
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center mb-4">
        <h5 class="text-h5">
          Review Hasil Scan Struk
        </h5>
        <VSpacer />
        <VBtn variant="tonal" :to="{ name: 'purchase-receipt-scan' }" prepend-icon="ri-arrow-left-line">
          Scan Struk Baru
        </VBtn>
      </div>
    </VCol>

    <!-- ── Loading awal ────────────────────────────────────────────────── -->
    <VCol v-if="loading" cols="12" class="text-center py-16">
      <VProgressCircular indeterminate color="primary" size="48" />
      <div class="text-body-2 text-medium-emphasis mt-3">
        Memuat data scan...
      </div>
    </VCol>

    <VCol v-else-if="error" cols="12">
      <VAlert type="error" variant="tonal">
        Gagal memuat data scan. Silakan muat ulang halaman.
      </VAlert>
    </VCol>

    <template v-else-if="scan">
      <!-- ── Status: sedang diproses OCR ──────────────────────────────── -->
      <VCol v-if="scan.status === 'processing'" cols="12" class="text-center py-16">
        <VProgressCircular indeterminate color="primary" size="48" />
        <div class="text-body-1 mt-4">
          Sedang membaca struk...
        </div>
        <div class="text-caption text-medium-emphasis">
          Biasanya selesai dalam beberapa detik. Halaman ini akan otomatis diperbarui.
        </div>
      </VCol>

      <!-- ── Status: gagal ────────────────────────────────────────────── -->
      <VCol v-else-if="scan.status === 'failed'" cols="12">
        <VAlert type="error" variant="tonal" class="mb-4">
          <div class="font-weight-medium mb-1">
            Gagal membaca struk
          </div>
          <div class="text-body-2">
            {{ scan.error_message || 'Terjadi kesalahan saat memproses struk.' }}
          </div>
        </VAlert>
        <VAlert type="info" variant="tonal">
          Silakan gunakan
          <RouterLink :to="{ name: 'purchase-receivings-create' }">
            form Purchase Receiving manual
          </RouterLink>
          untuk struk ini, atau coba scan ulang dengan foto yang lebih jelas.
        </VAlert>
      </VCol>

      <!-- ── Status: extracted / reviewed / applied -> tampilkan hasil ──── -->
      <template v-else>
        <!-- Foto struk -->
        <VCol cols="12" md="5">
          <VCard>
            <VCardTitle class="pt-4 px-4 text-body-1">
              Foto Struk Asli
            </VCardTitle>
            <VCardText class="pa-4">
              <VImg
                :src="scan.image_url"
                class="rounded border cursor-zoom-in"
                max-height="500"
                contain
                @click="zoomDialog = true"
              />
              <div class="text-caption text-medium-emphasis text-center mt-2">
                Klik foto untuk memperbesar
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Tabel hasil ekstraksi -->
        <VCol cols="12" md="7">
          <VCard>
            <VCardTitle class="pt-4 px-4 d-flex align-center text-body-1">
              Hasil Ekstraksi
              <VSpacer />
              <VChip size="small" variant="tonal">
                Confidence OCR: {{ scan.ocr_confidence != null ? Math.round(scan.ocr_confidence * 100) + '%' : '-' }}
              </VChip>
            </VCardTitle>

            <VCardText v-if="scan.status === 'applied'" class="px-4 pt-0">
              <VAlert type="success" variant="tonal" density="compact">
                Sudah dikonversi menjadi Purchase Receiving
                <RouterLink :to="{ name: 'purchase-receivings-id', params: { id: scan.purchase_receiving_id } }">
                  #{{ scan.purchase_receiving_id }}
                </RouterLink>.
              </VAlert>
            </VCardText>

            <VDivider />

            <VTable density="comfortable">
              <thead>
                <tr>
                  <th style="min-width: 220px">Bahan Baku</th>
                  <th style="min-width: 90px" class="text-end">Qty</th>
                  <th style="min-width: 110px">Satuan (teks)</th>
                  <th style="min-width: 130px" class="text-end">Harga Satuan</th>
                  <th style="min-width: 110px">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in scan.items" :key="item.id">
                  <td>
                    <VAutocomplete
                      :model-value="item.matched_ingredient_id"
                      :items="ingredients"
                      :loading="ingredientsLoading"
                      density="compact"
                      hide-details
                      no-data-text="Tidak ada bahan baku"
                      :disabled="scan.status === 'applied'"
                      @update:model-value="val => onIngredientChange(item, val)"
                    />
                    <div class="text-caption text-medium-emphasis mt-1">
                      OCR: "{{ item.raw_text_line }}"
                    </div>
                  </td>
                  <td>
                    <VTextField
                      v-model.number="item.extracted_qty"
                      type="number"
                      min="0"
                      step="0.01"
                      density="compact"
                      hide-details
                      class="text-end"
                      :disabled="scan.status === 'applied'"
                      @blur="saveItem(item)"
                    />
                  </td>
                  <td>
                    <VTextField
                      v-model="item.extracted_unit_text"
                      density="compact"
                      hide-details
                      :disabled="scan.status === 'applied'"
                      @blur="saveItem(item)"
                    />
                  </td>
                  <td>
                    <VTextField
                      v-model.number="item.extracted_unit_price"
                      type="number"
                      min="0"
                      step="100"
                      density="compact"
                      hide-details
                      class="text-end"
                      :disabled="scan.status === 'applied'"
                      @blur="saveItem(item)"
                    />
                  </td>
                  <td>
                    <VChip :color="statusColor(item.match_status)" size="small" variant="tonal">
                      <VProgressCircular
                        v-if="savingItemId === item.id"
                        indeterminate
                        size="12"
                        width="2"
                        class="me-1"
                      />
                      {{ statusLabel(item.match_status) }}
                    </VChip>
                  </td>
                </tr>
              </tbody>
            </VTable>

            <VDivider />

            <VCardActions class="pa-4">
              <VAlert
                v-if="convertErrors._general"
                type="error"
                variant="tonal"
                density="compact"
                class="w-100 mb-2"
              >
                {{ convertErrors._general[0] }}
              </VAlert>

              <VSpacer />

              <VTooltip v-if="convertDisabledReason" location="top">
                <template #activator="{ props: tooltipProps }">
                  <span v-bind="tooltipProps">
                    <VBtn color="primary" disabled>
                      Buat Purchase Receiving
                    </VBtn>
                  </span>
                </template>
                {{ convertDisabledReason }}
              </VTooltip>

              <VBtn
                v-else
                color="primary"
                :loading="converting"
                prepend-icon="ri-checkbox-circle-line"
                @click="convert"
              >
                Buat Purchase Receiving
              </VBtn>
            </VCardActions>
          </VCard>
        </VCol>
      </template>
    </template>

    <!-- ── Dialog zoom foto ─────────────────────────────────────────────── -->
    <VDialog v-model="zoomDialog" max-width="900">
      <VCard>
        <VCardText class="pa-2">
          <VImg :src="scan?.image_url" contain max-height="85vh" />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="zoomDialog = false">Tutup</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VRow>
</template>
